import type { ComponentType } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAuthStore } from '@/store/authStore'
import AccountShell from './AccountShell'
import MyCoursesTab from './tabs/MyCoursesTab'
import SkillsTab from './tabs/SkillsTab'
import CertificatesTab from './tabs/CertificatesTab'
import ProfileTab from './tabs/ProfileTab'
import WishlistTab from './tabs/WishlistTab'
import OrdersTab from './tabs/OrdersTab'
import InhouseTab from './tabs/InhouseTab'
import JobPostingsTab from './tabs/JobPostingsTab'
import JobApplicantsView from './tabs/JobApplicantsView'
import MyApplicationsTab from './tabs/MyApplicationsTab'
import SavedJobsTab from './tabs/SavedJobsTab'

const TAB_CONTENT: Record<string, ComponentType> = {
  courses: MyCoursesTab,
  skills: SkillsTab,
  certificates: CertificatesTab,
  profile: ProfileTab,
  wishlist: WishlistTab,
  orders: OrdersTab,
  inhouse: InhouseTab,
  'job-postings': JobPostingsTab,
  'my-applications': MyApplicationsTab,
  'saved-jobs': SavedJobsTab,
  // "ดูผู้สมัคร" ต่อตำแหน่งงานเดียว (ไม่ใช่หน้ารวมทุกตำแหน่ง) — เข้าจากปุ่ม "ดูผู้สมัคร (N)" ใน JobPostingsTab
  // ที่ส่ง ?applicants=<job_id> มาด้วย จึงจัดการแยกใน AccountPage แทนที่จะ map key ตรงๆ แบบ tab อื่น
}

const TAB_TITLES: Record<string, string> = {
  courses: 'คอร์สเรียนของฉัน',
  skills: 'สกิลของฉัน',
  certificates: 'ประกาศนียบัตร',
  profile: 'โปรไฟล์ของฉัน',
  wishlist: 'คอร์สที่ถูกใจ',
  orders: 'ประวัติคำสั่งซื้อ',
  inhouse: 'คำขอ In-house',
  'job-postings': 'ประกาศงานของบริษัท',
  'my-applications': 'ใบสมัครงานของฉัน',
  'saved-jobs': 'งานที่บันทึกไว้',
}

// คำอธิบายใต้หัวข้อ (แสดงในหัวหน้า AccountShell เดียว ไม่ต้องมีหัวข้อซ้ำในตัว tab เอง)
const TAB_SUBTITLES: Partial<Record<string, string>> = {
  skills: 'คุณจะได้รับสกิล เมื่อเรียนจบคอร์สที่เกี่ยวข้องกับสกิลนั้นครบทุกคอร์ส',
  'job-postings': 'จัดการตำแหน่งงานที่บริษัทของคุณเปิดรับสมัคร',
  'my-applications': 'ติดตามสถานะตำแหน่งงานที่คุณสมัครไป',
  'saved-jobs': 'ตำแหน่งงานที่คุณกดบันทึกไว้',
}

// tab ที่แต่ละประเภทบัญชีมีจริง (ตรงกับ COMPANY_TAB_GROUPS/INDIVIDUAL_TAB_GROUPS ใน AccountShell.tsx)
// ใช้เช็คว่า ?tab= ที่ได้มาถูกต้องกับบัญชีที่ login อยู่จริงไหม — กันกรณี ?tab= เป็นของบัญชีประเภทอื่น
// หลุดมาโชว์ผิด (เช่น เพิ่งสลับบัญชีจาก individual ไป company แล้ว path เดิมยังมี ?tab=courses ติดมา)
const INDIVIDUAL_TABS = ['courses', 'skills', 'certificates', 'wishlist', 'profile', 'orders', 'inhouse', 'my-applications', 'saved-jobs']
const COMPANY_TABS = ['job-postings', 'profile', 'orders', 'inhouse']

// หน้ารวม "บัญชีของฉัน" — ทุก sub-page สลับกันด้วย ?tab= (Shell แยกอยู่ AccountShell.tsx เพื่อให้ /profile/edit ใช้ร่วมกันได้)
export default function AccountPage() {
  const [searchParams] = useSearchParams()
  const customerType = useAuthStore((s) => s.customerType)
  const isCompany = customerType === 'COMPANY'
  const validTabs = isCompany ? COMPANY_TABS : INDIVIDUAL_TABS
  const defaultTab = isCompany ? 'job-postings' : 'courses'
  const requestedTab = searchParams.get('tab')
  const applicantsJobId = searchParams.get('applicants')

  // เมนู "ผู้สมัครงาน" ในแถบข้างพาไปหน้ารายการประกาศงาน (ดูทีละตำแหน่งผ่านปุ่ม "ดูผู้สมัคร" ตามที่ตกลงกันไว้ ไม่ทำหน้ารวมทุกตำแหน่ง)
  // เช็คจาก requestedTab ดิบ (ก่อน validate กับ whitelist) เพราะ 'job-applicants'/'job-postings'+applicants
  // ไม่ใช่ tab จริงในเมนู แค่เป็น route พิเศษ 2 อันนี้ — บัญชี individual จะไม่มี applicantsJobId อยู่แล้วจึงไม่หลุดเข้ามาที่นี่
  if (isCompany && requestedTab === 'job-applicants') {
    return (
      <AccountShell activeTab="job-postings" title={TAB_TITLES['job-postings']} subtitle="เลือกตำแหน่งงานที่ต้องการดูผู้สมัครจากรายการด้านล่าง">
        <JobPostingsTab />
      </AccountShell>
    )
  }

  if (isCompany && requestedTab === 'job-postings' && applicantsJobId) {
    return (
      <AccountShell activeTab="job-postings" title="" hideHeader>
        <JobApplicantsView jobId={Number(applicantsJobId)} />
      </AccountShell>
    )
  }

  // ?tab= ไม่มี หรือเป็น tab ของบัญชีประเภทอื่นที่บัญชีนี้ไม่มีสิทธิ์เข้า → fallback ไป default tab ที่ถูกต้องของบัญชีนี้เสมอ
  // (บั๊กที่เจอ: บัญชีนึง logout ค้างที่ ?tab=courses แล้วบัญชีอื่นมา login ต่อ ถูกพาไปหน้าที่ไม่ตรงกับประเภทบัญชีตัวเอง)
  const activeTab = requestedTab && validTabs.includes(requestedTab) ? requestedTab : defaultTab

  const ActiveContent = TAB_CONTENT[activeTab] || MyCoursesTab

  return (
    <AccountShell activeTab={activeTab} title={TAB_TITLES[activeTab] || TAB_TITLES.courses} subtitle={TAB_SUBTITLES[activeTab]}>
      <ActiveContent />
    </AccountShell>
  )
}
